import React, { useState, useCallback, useContext, useMemo, useEffect } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import messages from "gw-capability-fnol-common-react-ext/FNOL.messages";
import metadata from './LossLocationComponent.metadata.json5';

function LossLocationComponent(props) {
    const {
        onLocationChange,
        lossLocationIndex,
        predefinedLossLocations, 
        isReadOnly,
        setComponentValidation,
        showErrors
    } = props;
    
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const [currentLocationVM, updateCurrentLocationVM] = useState(viewModelService.create(
        {country: 'US'},
        'cc',
        'edge.capabilities.address.dto.AddressDTO'
    ));

    const {
        isComponentValid,
        invalidFields,
        registerComponentValidation
    } = useValidation('LossLocationComponent');

    const isCityEmptyCheck = useCallback(() => {
        if (_.isEmpty(_.get(currentLocationVM, 'value.city')) || _.isEmpty(_.get(currentLocationVM, 'value.state'))) {
            return false
        }
        return true
    }, [currentLocationVM])

    const getCountryValues = useCallback(() => {
        const availableCountries = _.get(currentLocationVM, 'country.aspects.availableValues', []);
        const filteredCountries = availableCountries.filter(country => country.code === 'US' || country.code === 'CA');
        const finalAvailableCountries = filteredCountries.map((country) => {
            return {
                code: country.code,
                name: translator({ id: country.name})
            }
        })
        return finalAvailableCountries
    }, [currentLocationVM, translator])

    const onCountryChange = (value, path) => {
        const newCurrentLocationVM = viewModelService.clone(currentLocationVM);
        _.set(newCurrentLocationVM, 'value.country', value);
        updateCurrentLocationVM(newCurrentLocationVM);
        onLocationChange(value, path);
    }

    useEffect(() => {
        let newCurrentLocationVM;
        if (lossLocationIndex !== predefinedLossLocations.length) {
            newCurrentLocationVM = viewModelService.clone(currentLocationVM);
            _.set(newCurrentLocationVM, 'value', predefinedLossLocations[lossLocationIndex]);
        } else {
            newCurrentLocationVM = viewModelService.create(
                {country: 'US', city: '', state: ''},
                'cc',
                'edge.capabilities.address.dto.AddressDTO'
            );
        }
        updateCurrentLocationVM(newCurrentLocationVM)
    }, [lossLocationIndex, predefinedLossLocations])
    
    useEffect(() => {
        registerComponentValidation(isCityEmptyCheck);
    }, [isCityEmptyCheck, registerComponentValidation]);

    useEffect(() => {
        if (setComponentValidation) {
            setComponentValidation(isComponentValid, 'LossLocationComponent');
        }
        return () => {
            if (setComponentValidation) {
                setComponentValidation(true, 'LossLocationComponent');
            }
        }
    }, [setComponentValidation, isComponentValid, showErrors, invalidFields, currentLocationVM])

    const overrideProps = useMemo(() => {
        return {
            '@field': {
                showOptional: true,
                labelPosition: "left",
                readOnly: isReadOnly
            },
            country: {
                availableValues: getCountryValues(),
                onValueChange: onCountryChange
            },
            lossLocationStateField: {
                label: {
                    defaultMessage: _.get(currentLocationVM, 'value.country') === 'CA' ? translator(messages.province) : translator(messages.state)
                },
            },
            lossLocationZipCodeField:{
                label: {
                    defaultMessage: _.get(currentLocationVM, 'value.country') === 'CA' ? translator(messages.postalCode) : translator(messages.zipCode)
                },
            }
        }
    }, [currentLocationVM, getCountryValues, isReadOnly, translator])

    const readValue = useCallback((fieldId, fieldPath) => {
        return readViewModelValue(
            metadata.pageContent, currentLocationVM, fieldId, fieldPath, overrideProps
        );
    }, [currentLocationVM, overrideProps]);

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={currentLocationVM}
            overrideProps={overrideProps}
            onValueChange={onLocationChange}
            resolveValue={readValue}
            showErrors={showErrors}
        />
    );
}
LossLocationComponent.propTypes = {
    isReadOnly: PropTypes.bool
};

LossLocationComponent.defaultTypes = {
    isReadOnly: false
};

export default LossLocationComponent;
